import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { match } from "ts-pattern";

import ImportCsv from "@/features/import-csv";
import type { ExportApiUrl } from "@/features/import-csv/types";
import { ImportCsvSearchSchema } from "@/features/import-csv/zod-schemas";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { ContractType } from "@/routes/_staff/contract/_list/-constants";

import useContractTypeParam from "./-hooks/useContractTypeParam";
import useLeaseTableRows from "./-lease/useLeaseTableRows";

const ContractImportCsv = () => {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { contractType } = useContractTypeParam();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.contract.importCsv.contractType",
  });

  const { t: tCompleteDialog } = useTranslation(defaultNS, {
    keyPrefix: "pages.contract.importCsv.completeDialog",
  });

  const title = match(contractType)
    .with(ContractType.LEASE, () => t("title.leaseAsset"))
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  // 今回はリース契約のみ対応
  const useTableRows = match(contractType)
    .with(ContractType.LEASE, () => useLeaseTableRows)
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  const exportApiUrl = match<keyof typeof ContractType, ExportApiUrl>(
    contractType,
  )
    .with(
      ContractType.LEASE,
      () => "/v2/contract-csv/lease-contract" as ExportApiUrl,
    )
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  const exportFilename = match(contractType)
    .with(ContractType.LEASE, () => t("filename.leaseAsset"))
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  const templateTitle = match(contractType)
    .with(ContractType.LEASE, () =>
      t("prepareCsvFile.templateTitle.leaseAsset"),
    )
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  const unit = match(contractType)
    .with(ContractType.LEASE, () => t("unit.leaseAssets"))
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  const updateExisting = match(contractType)
    .with(ContractType.LEASE, () =>
      t("prepareCsvFile.updateExisting.leaseAsset"),
    )
    .otherwise(() => {
      throw new Error("Invalid contract type");
    });

  return (
    <ImportCsv
      baseLink={`/contract/import-csv/${contractType}`}
      baseTitle={title}
      exportApiUrl={exportApiUrl}
      exportFilename={exportFilename}
      templateMessage={{
        templateTitle,
        updateExisting,
        completeLinkButton: tCompleteDialog("button"),
      }}
      completeLinkUrl="/contract"
      unit={unit}
      search={search}
      navigate={navigate}
      useTableRows={useTableRows}
    />
  );
};

export const Route = createFileRoute(
  "/_staff/contract/import-csv/$contractType/",
)({
  validateSearch: zodValidator(ImportCsvSearchSchema),
  component: ContractImportCsv,
});
